import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import * as employeeApi from '../../lib/api/employee';

const DOC_TYPE_OPTIONS = [
  { value: '', label: '전체 문서' },
  { value: 'HR_LEAVE', label: '휴가 신청' },
];

const REASON_TYPE_OPTIONS = [
  { value: 'MANUAL', label: '수동' },
  { value: 'LEAVE', label: '휴가' },
  { value: 'BUSINESS_TRIP', label: '출장' },
  { value: 'ABSENCE', label: '부재' },
];

const buildInitialValues = (data) => ({
  delegateUsrUuid: data?.delegateUsrUuid || '',
  docType: data?.docType || '',
  strtDt: data?.strtDt || dayjs().format('YYYY-MM-DD'),
  endDt: data?.endDt || dayjs().format('YYYY-MM-DD'),
  reasonTyp: data?.reasonTyp || 'MANUAL',
});

const ApprovalDelegationModal = ({
  open,
  data,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState(buildInitialValues(data));
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(buildInitialValues(data));
  }, [open, data]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await employeeApi.getEmployees({ useYn: 'Y' });
        setUserOptions(res.data.list || []);
      } catch (e) {
        setUserOptions([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [open]);

  const handleSubmit = () => {
    if (!form.delegateUsrUuid) {
      alert('대결자를 선택하세요.');
      return;
    }
    if (!form.strtDt || !form.endDt) {
      alert('대결 기간을 입력하세요.');
      return;
    }
    if (form.strtDt > form.endDt) {
      alert('대결 시작일과 종료일을 확인하세요.');
      return;
    }

    onSave({
      delegateUsrUuid: form.delegateUsrUuid,
      docType: form.docType || null,
      strtDt: form.strtDt,
      endDt: form.endDt,
      reasonTyp: form.reasonTyp,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data ? '대결 설정 수정' : '대결 설정 등록'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            지정한 기간 동안 현재 결재가 자동으로 대결자에게 배정됩니다.
          </Typography>

          <TextField
            select
            label="대결자"
            value={form.delegateUsrUuid}
            onChange={(e) => setForm((prev) => ({ ...prev, delegateUsrUuid: e.target.value }))}
            fullWidth
            disabled={loadingUsers}
          >
            {userOptions.map((user) => (
              <MenuItem key={user.usrUuid} value={user.usrUuid}>
                {user.usrNm} {user.dptNm ? `(${user.dptNm})` : ''}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="대상 문서"
            value={form.docType}
            onChange={(e) => setForm((prev) => ({ ...prev, docType: e.target.value }))}
            fullWidth
          >
            {DOC_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value || 'ALL'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="시작일"
              type="date"
              value={form.strtDt}
              onChange={(e) => setForm((prev) => ({ ...prev, strtDt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="종료일"
              type="date"
              value={form.endDt}
              onChange={(e) => setForm((prev) => ({ ...prev, endDt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <TextField
            select
            label="사유"
            value={form.reasonTyp}
            onChange={(e) => setForm((prev) => ({ ...prev, reasonTyp: e.target.value }))}
            fullWidth
          >
            {REASON_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">닫기</Button>
        <Button onClick={handleSubmit} variant="contained">
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalDelegationModal;
